"use client"

import { useMemo } from "react"
import { Control, FieldErrors, UseFormRegister, useWatch } from "react-hook-form"
import { FileIcon, LinkIcon, FileTextIcon } from "lucide-react"
import { FormSection } from "@/components/ui/form-section"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Controller } from "react-hook-form"
import { useI18n } from "@/lib/hooks/use-i18n"
import { NotebookUploadZone } from "@/components/notebooks/NotebookUploadZone"
import type { NotebookUploadedFile } from "@/lib/api/notebook-upload"

interface CreateSourceFormData {
  type: 'link' | 'upload' | 'text'
  title?: string
  url?: string
  content?: string
  file?: FileList | File
  notebooks?: string[]
  skills?: { skill_id: string; model_id?: string; locale?: string }[]
  embed: boolean
  build_graph: boolean
  async_processing: boolean
}

// Helper functions for batch URL parsing
function parseUrls(text: string): string[] {
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
}

function validateUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function parseAndValidateUrls(text: string): {
  valid: string[]
  invalid: { url: string; line: number }[]
} {
  const lines = text.split('\n')
  const valid: string[] = []
  const invalid: { url: string; line: number }[] = []

  lines.forEach((line, index) => {
    const trimmed = line.trim()
    if (trimmed.length === 0) return // skip empty lines

    if (validateUrl(trimmed)) {
      valid.push(trimmed)
    } else {
      invalid.push({ url: trimmed, line: index + 1 })
    }
  })

  return { valid, invalid }
}

interface SourceTypeStepProps {
  control: Control<CreateSourceFormData>
  register: UseFormRegister<CreateSourceFormData>
  errors: FieldErrors<CreateSourceFormData>
  urlValidationErrors?: { url: string; line: number }[]
  onClearUrlErrors?: () => void
  onFilesUploaded?: (files: NotebookUploadedFile[]) => void
  uploadedFileCount?: number
}

const MAX_BATCH_SIZE = 50

export function SourceTypeStep({ control, register, errors, urlValidationErrors, onClearUrlErrors, onFilesUploaded, uploadedFileCount = 0 }: SourceTypeStepProps) {
  const { t } = useI18n()

  const SOURCE_TYPES = useMemo(() => [
    {
      value: 'link' as const,
      label: t('addSourceDialog.type.link.label'),
      icon: LinkIcon,
      description: t('addSourceDialog.type.link.description'),
    },
    {
      value: 'upload' as const,
      label: t('addSourceDialog.type.upload.label'),
      icon: FileIcon,
      description: t('addSourceDialog.type.upload.description'),
    },
    {
      value: 'text' as const,
      label: t('addSourceDialog.type.text.label'),
      icon: FileTextIcon,
      description: t('addSourceDialog.type.text.description'),
    },
  ], [t])

  // Watch the selected type and inputs to detect batch mode
  const selectedType = useWatch({ control, name: 'type' })
  const urlInput = useWatch({ control, name: 'url' })

  // Batch mode detection
  const { isBatchMode, itemCount, urlCount, fileCount } = useMemo(() => {
    let urlCount = 0
    let fileCount = 0

    if (selectedType === 'link' && urlInput) {
      const urls = parseUrls(urlInput)
      urlCount = urls.length
    }

    if (selectedType === 'upload') {
      fileCount = uploadedFileCount
    }

    const isBatchMode = urlCount > 1 || fileCount > 1
    const itemCount = selectedType === 'link' ? urlCount : fileCount

    return { isBatchMode, itemCount, urlCount, fileCount }
  }, [selectedType, urlInput, uploadedFileCount])

  // Check for batch size limit
  const isOverLimit = itemCount > MAX_BATCH_SIZE
  return (
    <div className="space-y-6">
      <FormSection
        title={t('addSourceDialog.type.title')}
        description={t('addSourceDialog.type.description')}
      >
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Tabs
              value={field.value || ''}
              onValueChange={(value) => field.onChange(value as 'link' | 'upload' | 'text')}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3">
                {SOURCE_TYPES.map((type) => {
                  const Icon = type.icon
                  return (
                    <TabsTrigger key={type.value} value={type.value} className="gap-2">
                      <Icon className="h-4 w-4" />
                      {type.label}
                    </TabsTrigger>
                  )
                })}
              </TabsList>

              {SOURCE_TYPES.map((type) => (
                <TabsContent key={type.value} value={type.value} className="mt-4">
                  <p className="text-sm text-muted-foreground mb-4">{type.description}</p>

                  {/* Type-specific fields */}
                  {type.value === 'link' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="url">{t('addSourceDialog.type.link.labelUrl')}</Label>
                        {urlCount > 0 && (
                          <Badge variant={isOverLimit ? "destructive" : "secondary"}>
                            {t(urlCount > 1 ? 'addSourceDialog.type.link.urlCount_plural' : 'addSourceDialog.type.link.urlCount', { count: urlCount })}
                            {isOverLimit && ` (${t('addSourceDialog.type.maxSize', { size: MAX_BATCH_SIZE })})`}
                          </Badge>
                        )}
                      </div>
                      <Textarea
                        id="url"
                        {...register('url', {
                          onChange: () => onClearUrlErrors?.()
                        })}
                        placeholder={t('addSourceDialog.type.link.placeholder')}
                        rows={urlCount > 1 ? 6 : 2}
                        className="max-h-48 overflow-y-auto font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('addSourceDialog.type.link.batchHint')}
                      </p>
                      {errors.url && (
                        <p className="text-sm text-destructive mt-1">{errors.url.message}</p>
                      )}
                      {urlValidationErrors && urlValidationErrors.length > 0 && (
                        <div className="mt-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
                          <p className="text-sm font-medium text-destructive mb-2">
                            {t('addSourceDialog.type.link.invalidUrlError')}
                          </p>
                          <ul className="space-y-1">
                            {urlValidationErrors.map((error, idx) => (
                              <li key={idx} className="text-xs text-destructive flex items-start gap-2">
                                <span className="font-mono bg-destructive/20 px-1 rounded">
                                  {t('addSourceDialog.type.link.line', { line: error.line })}
                                </span>
                                <span className="truncate">{error.url}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs text-muted-foreground mt-2">
                            {t('addSourceDialog.type.link.fixInvalidUrlHint')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {type.value === 'upload' && (
                    <div>
                      <NotebookUploadZone
                        dir="notebook-source"
                        onFilesUploaded={onFilesUploaded}
                        compact
                      />
                    </div>
                  )}

                  {type.value === 'text' && (
                    <div>
                      <Label htmlFor="content" className="mb-2 block">{t('addSourceDialog.type.text.labelContent')}</Label>
                      <Textarea
                        id="content"
                        {...register('content')}
                        placeholder={t('addSourceDialog.type.text.placeholder')}
                        rows={6}
                        className="max-h-64 overflow-y-auto"
                      />
                      {errors.content && (
                        <p className="text-sm text-destructive mt-1">{errors.content.message}</p>
                      )}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        />
        {errors.type && (
          <p className="text-sm text-destructive mt-1">{errors.type.message}</p>
        )}
      </FormSection>

      {/* Hide title field in batch mode - titles will be auto-generated */}
      {!isBatchMode && (
        <FormSection
          title={selectedType === 'text' ? t('addSourceDialog.title.required') : t('addSourceDialog.title.optional')}
          description={selectedType === 'text'
            ? t('addSourceDialog.title.textHint')
            : t('addSourceDialog.title.genericHint')
          }
        >
          <Input
            id="title"
            {...register('title')}
            placeholder={t('addSourceDialog.title.placeholder')}
          />
          {errors.title && (
            <p className="text-sm text-destructive mt-1">{errors.title.message}</p>
          )}
        </FormSection>
      )}

      {/* Batch mode indicator */}
      {isBatchMode && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="default">{t('addSourceDialog.batchMode.title')}</Badge>
            <span className="text-sm font-medium">
              {t('addSourceDialog.batchMode.description', { count: itemCount, type: selectedType === 'link' ? 'URLs' : 'files' })}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('addSourceDialog.batchMode.hint')}
          </p>
        </div>
      )}
    </div>
  )
}
